if (!customElements.get('product-configurator')) {
  customElements.define(
    'product-configurator',
    class ProductConfigurator extends HTMLElement {
      constructor() {
        super();
        
        this.optionsContainer = this.querySelector('.configurator__options');
        this.optionSteps = this.querySelectorAll('.configurator__options .configurator__step');
        this.productSlotsContainer = this.querySelector('product-configurator-products');
        if (this.productSlotsContainer) {
          this.productSlots = this.productSlotsContainer.querySelectorAll('.configurator__step-product-placeholder');
          this.productChoices = this.productSlotsContainer.querySelectorAll('.configurator__choice');
        }
        this.submitButton = this.querySelector('.product-form__submit');
        if (this.querySelector('#configurator-product-template')) {
          this.template = this.querySelector('#configurator-product-template').content;
        }
        this.priceContainer = this.querySelector('.configurator__setup-prices');

        // Sticky state handling for the setup sidebar
        this.setupInner = this.querySelector('.configurator__setup-inner');
        this.stickyAddTimer = null;
        if (this.setupInner) {
          this.boundUpdateStickyClass = this.updateStickyClass.bind(this);
          window.addEventListener('scroll', this.boundUpdateStickyClass, { passive: true });
          window.addEventListener('resize', this.boundUpdateStickyClass);
          // Run once after initial layout
          setTimeout(this.boundUpdateStickyClass);
        }

        this.addEventListener('click', this.onOptionClick.bind(this));
        if (this.productSlotsContainer) {
          this.productSlotsContainer.addEventListener('click', this.onSlotClick.bind(this));
        } else {
          this.addEventListener('change', this.calculateLeanPrice.bind(this));
          setTimeout(this.calculateLeanPrice.bind(this));
        }

        if (this.submitButton) {
          this.checkSubmitButtonState();
        }
      }

      updateStickyClass() {
        if (!this.setupInner) return;

        const computed = getComputedStyle(this.setupInner);
        const stickyTop = parseFloat(computed.top) || 0;

        // Find the nearest ancestor that actually has a layout box (skip display: contents)
        let container = this.setupInner.parentElement;
        while (container && getComputedStyle(container).display === 'contents') {
          container = container.parentElement;
        }
        // If no suitable container, treat viewport as the boundary and skip bottom lock check
        let parentRect = null;
        if (container) parentRect = container.getBoundingClientRect();
        const elRect = this.setupInner.getBoundingClientRect();

        // Viewport threshold: stick when element's top is within top 10% of viewport
        const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
        const thresholdPx = viewportHeight * 0.1;
        const withinTopThreshold = elRect.top <= thresholdPx && window.scrollY > 0;
        const notPastBottom = !parentRect || (parentRect.bottom - elRect.height >= 0);
        const isStuck = withinTopThreshold && notPastBottom;

        // No time delay; threshold is positional, so apply immediately when inside top band
        if (isStuck) {
          if (!this.setupInner.classList.contains('configurator__setup-inner--stuck') && !this.stickyAddTimer) {
            // Prevent header reveal exactly when applying
            const headerEl = document.querySelector('sticky-header');
            if (headerEl) headerEl.dispatchEvent(new CustomEvent('preventHeaderReveal'));
            this.setupInner.classList.add('configurator__setup-inner--stuck');
          }
        } else {
          if (this.stickyAddTimer) {
            clearTimeout(this.stickyAddTimer);
            this.stickyAddTimer = null;
          }
          this.setupInner.classList.remove('configurator__setup-inner--stuck');
        }
      }

      disconnectedCallback() {
        if (this.boundUpdateStickyClass) {
          window.removeEventListener('scroll', this.boundUpdateStickyClass);
          window.removeEventListener('resize', this.boundUpdateStickyClass);
        }
        if (this.stickyAddTimer) {
          clearTimeout(this.stickyAddTimer);
          this.stickyAddTimer = null;
        }
      }

      calculateLeanPrice() {
        if (this.productSlotsContainer) return;
    
        let totalPrice = parseFloat(this.dataset.price) || 0;
        if (isNaN(totalPrice)) totalPrice = 0;
    
        const selectedOptions = this.querySelectorAll('input[type="radio"]:checked, select.select__select');
    
        selectedOptions.forEach((input) => {
          let price = 0;
          if (input.tagName === 'SELECT') {
            const selectedOption = input.options[input.selectedIndex];
            if (selectedOption && selectedOption.dataset.price) {
              price = parseFloat(selectedOption.dataset.price);
            }
          } else {
            price = parseFloat(input.dataset.price);
          }
    
          if (!isNaN(price)) {
            totalPrice += price;
          }
        });
    
        const priceContainer = document.querySelector(`#price-${this.dataset.sectionId}`);
        const priceElement = priceContainer.querySelector('.price-item--regular');
    
        if (priceElement && window.Currency) {
          priceElement.textContent = window.Currency.formatMoney(totalPrice, window.currency.moneyFormat).replace(',00', '');
        }
      }

      onOptionClick(event) {
        const button = event.target.closest('[data-variant-id]');
        if (!button || button.closest('.configurator-product')) return;

        event.preventDefault();
        this.handleOptionSelection(button);
      }
      
      handleOptionSelection(button) {
        const card = button.closest('.card-wrapper');
        const step = card.closest('.configurator__step');
        const stepIndex = Array.from(this.optionSteps).indexOf(step);
        
        if (stepIndex === -1) return;

        const mutate = () => {
          const previouslySelectedCard = step.querySelector('[data-selected="true"]');
          if (previouslySelectedCard) {
            previouslySelectedCard.removeAttribute('data-selected');
          }

          const slot = this.productSlots[stepIndex];
          slot.querySelector('.configurator-product')?.remove();
          
          const placeholder = slot.querySelector('.configurator__placeholder');
          if (placeholder) placeholder.classList.add('hidden');
          
          const variantId = button.dataset.variantId;
          const newProductElement = this.createProductElement(card, variantId, stepIndex);
          slot.appendChild(newProductElement);

          this.productSlots[stepIndex].classList.remove('configurator__step--empty');

          this.productSlotsContainer.querySelector(`input[data-step-index='${stepIndex}']`)?.remove();
          this.createHiddenInput(variantId, stepIndex);
          
          card.dataset.selected = 'true';
          this.checkSubmitButtonState();
          this.showNextStep(stepIndex);
          this.updatePrice();
        };

        if (document.startViewTransition) {
          document.startViewTransition(mutate);
        } else {
          mutate();
        }
      }

      showNextStep(currentStepIndex) {
        const nextStepIndex = currentStepIndex + 1;
        if (nextStepIndex < this.optionSteps.length) {
          this.setActiveStep(nextStepIndex);
        }
      }
      
      onSlotClick(event) {
        const removeButton = event.target.closest('.configurator-product__remove');
        if (removeButton) {
          this.removeProduct(removeButton);
          return;
        }

        const clickedChoice = event.target.closest('.configurator__choice');
        if (clickedChoice) {
          const stepIndex = parseInt(clickedChoice.dataset.stepIndex, 10);
          if (!isNaN(stepIndex)) {
            this.setActiveStep(stepIndex);
          }
        }
      }

      setActiveStep(stepIndex) {
        this.optionSteps.forEach((step, index) => {
          step.classList.toggle('hidden', index !== stepIndex);
        });
        this.productChoices.forEach((slot, index) => {
          slot.classList.toggle('active', index === stepIndex);
        });

        const activeStep = this.optionSteps[stepIndex];
        if (activeStep) {
          const isMobile = window.matchMedia('(max-width: 749px)').matches;
          let offset = 0;

          if (isMobile) {
            const stickyEl = this.querySelector('.configurator__setup-inner--stuck');
            if (stickyEl) {
              offset = stickyEl.offsetHeight;
            }
          }

          const headerOffset = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--header-bottom-position').replace('px', '')) || 0;
          offset += headerOffset;

          const elementPosition = activeStep.getBoundingClientRect().top;
          const offsetPosition = elementPosition + window.scrollY - offset;

          window.scrollTo({
            top: offsetPosition,
            behavior: 'smooth'
          });
        }
      }

      createProductElement(card, variantId, stepIndex) {
        const image = card.querySelector('img');
        const titleElement = card.querySelector('.card-information__inner');
        
        const newContent = this.template.cloneNode(true);
        const productElement = newContent.querySelector('.configurator-product');
        productElement.dataset.variantId = variantId;
        productElement.dataset.stepIndex = stepIndex;
        productElement.dataset.price = card.querySelector('[data-price]').dataset.price;

        const clonedImage = image.cloneNode(true);

        productElement.querySelector('.configurator-product__image').appendChild(clonedImage);
        productElement.querySelector('.configurator-product__info').appendChild(titleElement.cloneNode(true));

        return newContent;
      }
      
      createHiddenInput(variantId, stepIndex) {
        const checkbox = Object.assign(document.createElement('input'), {
          type: 'checkbox',
          name: 'variant-id',
          value: variantId,
          checked: true,
          hidden: true
        });
        checkbox.dataset.stepIndex = stepIndex;
        this.productSlotsContainer.appendChild(checkbox);
      }

      removeProduct(removeButton) {
        const productToRemove = removeButton.closest('.configurator-product');
        const stepIndex = parseInt(productToRemove.dataset.stepIndex, 10);

        // Show the placeholder
        const placeholder = this.productSlots[stepIndex].querySelector('.configurator__placeholder');
        if (placeholder) placeholder.classList.remove('hidden');

        productToRemove.remove();
        this.productSlots[stepIndex].classList.add('configurator__step--empty');


        const checkboxToRemove = this.productSlotsContainer.querySelector(`input[data-step-index='${stepIndex}']`);
        if (checkboxToRemove) {
          checkboxToRemove.remove();
        }

        const stepElement = this.optionSteps[stepIndex];
        const selectedCard = stepElement.querySelector('[data-selected="true"]');
        if (selectedCard) {
            selectedCard.removeAttribute('data-selected');
        }

        this.checkSubmitButtonState();
        this.setActiveStep(stepIndex);
        this.updatePrice();
      }

      checkSubmitButtonState() {
        const selectedCount = this.productSlotsContainer.querySelectorAll('input[type="checkbox"]:checked').length;
        const isDisabled = selectedCount < 2;

        if (this.submitButton.disabled && !isDisabled) {
          const headerOffset = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--header-bottom-position').replace('px', '')) || 0;
          const offset = headerOffset;

          const elementPosition = this.setupInner.getBoundingClientRect().top;
          const offsetPosition = elementPosition + window.scrollY - offset;

          window.scrollTo({
            top: offsetPosition,
            behavior: 'smooth'
          });
        }

        this.submitButton.disabled = isDisabled;
      }

      updatePrice() {
        const selectedProducts = this.productSlotsContainer.querySelectorAll('.configurator-product');
        const prices = Array.from(selectedProducts).map(p => parseFloat(p.dataset.price));
        const regularPriceContainer = this.priceContainer.querySelector('.configurator__setup-prices--regular-price');
        const discountedPriceContainer = this.priceContainer.querySelector('.configurator__setup-prices--discounted-price');
        const discountLabel = this.priceContainer.querySelector('.configurator__setup-prices--discount-label span');
        const discountPercentage = parseFloat(this.dataset.discountPercentage);

        if (prices.length === 0) {
            return;
        }


        const totalPrice = prices.reduce((a, b) => a + b, 0);
        
        if (prices.length === 1) {
            regularPriceContainer.textContent = '';
            discountLabel.textContent = '';
            discountedPriceContainer.textContent = window.Currency.formatMoney(totalPrice, window.currency.moneyFormat);
            return;
        }

        let discountedPrice = totalPrice;
        if (prices.length >= 2 && discountPercentage > 0) {
            const cheapestProductPrice = Math.min(...prices);
            const discountAmount = cheapestProductPrice * (discountPercentage / 100);
            discountedPrice = totalPrice - discountAmount;

            regularPriceContainer.textContent = window.Currency.formatMoney(totalPrice, window.currency.moneyFormat);
            discountLabel.textContent = window.Currency.formatMoney(discountAmount, window.currency.moneyFormat);
            discountedPriceContainer.textContent = window.Currency.formatMoney(discountedPrice, window.currency.moneyFormat);
        } else {
            regularPriceContainer.textContent = '';
            discountLabel.textContent = '';
            discountedPriceContainer.textContent = window.Currency.formatMoney(totalPrice, window.currency.moneyFormat);
        }
    }
    }
  );
}


if (!customElements.get('product-configurator-filter')) {
  customElements.define(
    'product-configurator-filter',
    class ProductConfiguratorFilter extends HTMLElement {
      constructor() {
        super();
        this.inputElement = this.querySelector('input[type="search"]');
        this.brandButtons = this.querySelectorAll('.configurator__step-option--filter');
        this.step = this.closest('.configurator__step');
        this.productOptions = this.step.querySelectorAll('.configurator__step-option:not(.configurator__step-option--filter)');

        this.addEventListener('click', this.onBrandClick.bind(this));
        if (this.inputElement) {
          this.inputElement.addEventListener('input', this.filter.bind(this));
        }
      }

      onBrandClick(event) {
        const brandButton = event.target.closest('button');
        if (!brandButton || !brandButton.classList.contains('configurator__step-option--filter')) return;
        
        this.brandButtons.forEach(button => button.removeAttribute('data-active'));
        brandButton.setAttribute('data-active', 'true');
        
        this.filter();
      }

      filter() {
        const query = this.inputElement?.value.toLowerCase().trim() || '';
        const activeBrand = this.querySelector('[data-active="true"]').dataset.brand;
        const handleizedQuery = query.replace(/\s+/g, '-');

        this.productOptions.forEach(option => {
          const productBrand = option.dataset.brand;
          const text = option.querySelector('.card-information__inner')?.textContent.toLowerCase().trim() || '';
          
          const brandMatch = activeBrand === 'all' || productBrand === activeBrand;
          const searchMatch = this.fuzzyMatch(text, query) || this.fuzzyMatch(productBrand, handleizedQuery);
          
          const isVisible = brandMatch && searchMatch;
          option.classList.toggle('hidden', !isVisible);
        });
      }

      fuzzyMatch(text, query) {
        if (!query) return true;
        let ti = 0;
        let qi = 0;
        while (ti < text.length && qi < query.length) {
          if (text[ti] === query[qi]) qi++;
          ti++;
        }
        return qi === query.length;
      }
    }
  );
}

if (!customElements.get('configurator-card')) {
  customElements.define(
    'configurator-card',
    class ConfiguratorCard extends HTMLElement {
      constructor() {
        super();
        this.productHandle = this.dataset.handle;
        this.sectionName = this.dataset.section;
        this.onVariantChange = this.onVariantChange.bind(this);
        this.bindVariantSelects();
      }

      bindVariantSelects() {
        this.querySelectorAll('select.select__select').forEach((select) => {
          select.addEventListener('change', this.onVariantChange);
        });
      }

      getSelectedOptionValueIds() {
        return Array.from(this.querySelectorAll('select.select__select'))
          .map((select) => select.options[select.selectedIndex]?.dataset.optionValueId)
          .filter(Boolean);
      }

      onVariantChange() {
        const optionValueIds = this.getSelectedOptionValueIds();
        if (!optionValueIds.length) return;

        this.loadSection(optionValueIds);
      }

      loadSection(optionValueIds) {
        if (!this.productHandle || !this.sectionName) {
          console.error('ConfiguratorCard: Missing product handle or section name. Make sure data-handle and data-section attributes are present.');
          return;
        }

        // Use option_values so multi-option products (e.g. Color + Pump type)
        // resolve to the correct combined variant, not only the first option's match.
        const url = `/products/${this.productHandle}?option_values=${optionValueIds.join(',')}&section_id=${this.sectionName}`;

        this.classList.add('loading');
        this.abortController?.abort();
        this.abortController = new AbortController();

        fetch(url, { signal: this.abortController.signal })
          .then(response => {
              if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
              return response.text();
          })
          .then(html => {
            if (!html.trim()) {
              console.error('ConfiguratorCard: Empty response from section rendering API for URL:', url);
              return;
            }

            this.innerHTML = html;
            this.bindVariantSelects();
          })
          .catch(error => {
            if (error.name === 'AbortError') return;
            console.error('ConfiguratorCard: Error fetching section:', error);
          })
          .finally(() => {
            this.classList.remove('loading');
          });
      }
    }
  );
}

if (!customElements.get('custom-product-option')) {
  customElements.define(
    'custom-product-option',
    class CustomProductOption extends HTMLElement {
      constructor() {
        super();
        this.select = this.querySelector('select.select__select');
        this.productContents = this.querySelectorAll('.custom-product-option__product-content');
        this.radioInputs = this.querySelectorAll('input[type="radio"]');
        this.detailsElement = this.querySelector('details');
        if(this.detailsElement){  
          this.selectedValueElement = this.detailsElement.querySelector('[data-selected-value]');
        }

        if (this.select) {
          this.select.addEventListener('change', this.onProductChange.bind(this));
        }
        
        this.radioInputs.forEach(input => {
          input.addEventListener('change', this.onRadioChange.bind(this));
        });
      }

            _handleSelection(title) {
        if (this.detailsElement) {
          this.detailsElement.removeAttribute('open');
        }

        if (this.selectedValueElement) {
          this.selectedValueElement.textContent = title;
        }

        const nextOption = this.closest('custom-product-option').nextElementSibling;
        if (nextOption && nextOption.tagName === 'CUSTOM-PRODUCT-OPTION') {
          const nextDetails = nextOption.querySelector('details');
          if (nextDetails) {
            nextDetails.setAttribute('open', '');
          }
        }
      }

      onProductChange(event) {
        const selectedOption = event.target.options[event.target.selectedIndex];
        const selectedProductId = selectedOption.value;

        this.productContents.forEach(content => {
          const wasVisible = !content.classList.contains('hidden');
          content.classList.add('hidden');
          if (wasVisible && content.dataset.productId !== selectedProductId) {
            const radio = content.querySelector('input[type="radio"]:checked');
            if (radio) {
              radio.checked = false;
            }
            if (this.selectedValueElement) {
              this.selectedValueElement.textContent = '';
            }
          }
        });

        if (selectedProductId) {
          const selectedContent = this.querySelector(`.custom-product-option__product-content[data-product-id="${selectedProductId}"]`);
          if (selectedContent) {
            selectedContent.classList.remove('hidden');
          }
        }

        if (selectedOption.hasAttribute('data-free-option') || selectedOption.hasAttribute('data-single-variant')) {
          this._handleSelection(selectedOption.textContent.trim());
        }
      }
      
      onRadioChange(event) {
        this._handleSelection(event.target.dataset.title);
      }
    }
  );
}
